import React, { useMemo } from 'react';
import { Table, Avatar, Empty, Select, Tooltip, Card, Space } from 'antd';
import {
  UserOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  StopOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import moment from 'moment-jalaali';
import {
  getAttendancePalette,
  resolveAttendanceVisualKey,
  resolveIsLate,
} from '../../../utils/attendanceStatusStyles';

const { Option } = Select;

const statusIcons = {
  present: <CheckCircleOutlined />,
  late: <ClockCircleOutlined />,
  absent: <CloseCircleOutlined />,
  leave: <FileTextOutlined />,
  half_day_leave: <FileTextOutlined />,
  half_day: <ExclamationCircleOutlined />,
  holiday: <CalendarOutlined />,
  day_off: <StopOutlined />,
  mission: <EnvironmentOutlined />,
};

const AttendanceByHourView = ({
  data,
  loading = false,
  filters = {},
  onFiltersChange,
  departments = [],
  designations = [],
  employees = [],
}) => {
  moment.locale('fa');
  const jYear = filters.year || moment().format('jYYYY');
  const jMonthName = filters.monthName || 'آذر';
  const department = filters.department || 'all';
  const jobTitle = filters.designation || 'all';
  const employee = filters.employee || 'all';

  const daysInMonth = data?.meta?.daysInMonth || 30;

  const legendItems = [
    { key: 'present', label: 'حضور به‌موقع' },
    { key: 'late', label: 'تأخیر' },
    { key: 'absent', label: 'غیبت' },
    { key: 'half_day', label: 'نیم‌روز' },
    { key: 'holiday', label: 'تعطیل رسمی' },
    { key: 'day_off', label: 'تعطیل هفتگی' },
    { key: 'leave', label: 'مرخصی' },
    { key: 'mission', label: 'مأموریت' },
  ];

  const renderStatusCell = (val) => {
    if (!val) return <span style={{ color: '#d9d9d9' }}>-</span>;

    const visualKey = resolveAttendanceVisualKey(val);
    const palette = getAttendancePalette(visualKey);
    const isLate = typeof val === 'object' ? resolveIsLate(val) : visualKey === 'late';
    const time =
      typeof val === 'object' ? val.formatted || val.total_formatted || '' : '';
    const missionDest =
      typeof val === 'object'
        ? val.destination || val.mission?.destination || null
        : null;
    const missionTrip =
      typeof val === 'object'
        ? val.trip_type || val.mission?.trip_type || null
        : null;

    // حاضر / تأخیر: نمایش ساعت + رنگ استاندارد
    if (visualKey === 'present' || visualKey === 'late') {
      return (
        <Tooltip
          title={
            <span style={{ fontFamily: 'CustomFont' }}>
              {isLate ? 'ورود با تأخیر' : 'حضور به‌موقع'}
              {time ? ` — ${time}` : ''}
            </span>
          }
        >
          <div
            style={{
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 40,
              minHeight: 28,
              padding: '2px 4px',
              borderRadius: 6,
              backgroundColor: palette?.bg || '#ecfdf5',
              color: palette?.color || '#047857',
              border: `1px solid ${palette?.border || '#a7f3d0'}`,
              fontFamily: 'CustomFont',
              fontSize: 11,
              fontWeight: isLate ? 700 : 600,
              lineHeight: 1.2,
            }}
          >
            <span>{time || (isLate ? 'تأخیر' : 'حاضر')}</span>
            {isLate ? (
              <span
                style={{
                  marginTop: 2,
                  fontSize: 9,
                  padding: '0 4px',
                  borderRadius: 999,
                  background: '#fde68a',
                  color: '#92400e',
                  fontWeight: 600,
                }}
              >
                تأخیر
              </span>
            ) : null}
          </div>
        </Tooltip>
      );
    }

    if (!palette) {
      return (
        <span style={{ color: '#d9d9d9', fontSize: 11 }}>
          {typeof val === 'string' ? val : '-'}
        </span>
      );
    }

    if (visualKey === 'mission') {
      return (
        <Tooltip
          title={
            <span style={{ fontFamily: 'CustomFont' }}>
              مأموریت
              {missionTrip === 'outside_city'
                ? ' برون‌شهری'
                : missionTrip === 'inside_city'
                  ? ' درون‌شهری'
                  : ''}
              {missionDest ? ` — ${missionDest}` : ''}
            </span>
          }
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 28,
              height: 28,
              padding: '0 4px',
              borderRadius: 6,
              backgroundColor: palette.bg,
              color: palette.color,
              border: `1px solid ${palette.border}`,
              fontSize: 14,
            }}
          >
            {statusIcons.mission}
          </div>
        </Tooltip>
      );
    }

    return (
      <Tooltip title={<span style={{ fontFamily: 'CustomFont' }}>{palette.title}</span>}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 6,
            backgroundColor: palette.bg,
            color: palette.color,
            border: `1px solid ${palette.border}`,
            fontSize: 14,
          }}
        >
          {statusIcons[visualKey] || palette.text?.[0]}
        </div>
      </Tooltip>
    );
  };

  const generateDayColumns = () => {
    const columns = [];
    for (let day = 1; day <= daysInMonth; day += 1) {
      columns.push({
        title: <span style={{ fontSize: 12 }}>{day}</span>,
        dataIndex: `day${day}`,
        key: `day${day}`,
        align: 'center',
        width: 56,
        render: (val) => renderStatusCell(val),
      });
    }
    return columns;
  };

  const attendanceData = useMemo(() => {
    const list = data?.employees || [];
    return list.map((emp) => {
      const row = {
        id: emp.id,
        employee: emp.name,
        jobTitle: emp.designation_name || emp.designation?.name || '',
        avatar: emp.avatar,
        total: emp.total_formatted || emp.totalMinutesFormatted || emp.total_minutes || '00:00',
      };
      const days = emp.days || {};
      Object.entries(days).forEach(([day, val]) => {
        row[`day${day}`] = val;
      });
      return row;
    });
  }, [data]);

  const columns = [
    {
      title: 'کارمند',
      dataIndex: 'employee',
      key: 'employee',
      fixed: 'right',
      width: 200,
      render: (text, record) => (
        <div className="flex items-center gap-2">
          <Avatar src={record.avatar} icon={<UserOutlined />} size="small" />
          <div>
            <div style={{ fontFamily: 'CustomFont' }}>{text}</div>
            {record.jobTitle && (
              <div className="text-xs text-gray-500" style={{ fontFamily: 'CustomFont' }}>
                {record.jobTitle}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'مجموع',
      dataIndex: 'total',
      key: 'total',
      fixed: 'right',
      width: 100,
      align: 'center',
      render: (text) => (
        <span style={{ fontFamily: 'CustomFont', fontWeight: 600, color: '#1890ff' }}>{text}</span>
      ),
    },
    ...generateDayColumns(),
  ];

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 mb-4">
        <Space wrap className="w-full" size="middle">
          <div className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">سال:</span>
            <Select value={jYear} onChange={(v) => onFiltersChange?.({ year: v })} style={{ width: 120, fontFamily: 'CustomFont' }}>
              <Option value="1404">۱۴۰۴</Option>
              <Option value="1403">۱۴۰۳</Option>
              <Option value="1402">۱۴۰۲</Option>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">ماه:</span>
            <Select value={jMonthName} onChange={(v) => onFiltersChange?.({ monthName: v })} style={{ width: 140, fontFamily: 'CustomFont' }}>
              <Option value="فروردین">فروردین</Option>
              <Option value="اردیبهشت">اردیبهشت</Option>
              <Option value="خرداد">خرداد</Option>
              <Option value="تیر">تیر</Option>
              <Option value="مرداد">مرداد</Option>
              <Option value="شهریور">شهریور</Option>
              <Option value="مهر">مهر</Option>
              <Option value="آبان">آبان</Option>
              <Option value="آذر">آذر</Option>
              <Option value="دی">دی</Option>
              <Option value="بهمن">بهمن</Option>
              <Option value="اسفند">اسفند</Option>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">کارمند:</span>
            <Select value={employee} onChange={(v) => onFiltersChange?.({ employee: v, userId: v })} style={{ width: 200, fontFamily: 'CustomFont' }} showSearch optionFilterProp="children">
              <Option value="all">همه</Option>
              {employees.map((e) => (
                <Option key={e.id} value={String(e.id)}>{e.name}</Option>
              ))}
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">دپارتمان:</span>
            <Select value={department} onChange={(v) => onFiltersChange?.({ department: v })} style={{ width: 180, fontFamily: 'CustomFont' }}>
              <Option value="all">همه</Option>
              {departments.map((d) => (
                <Option key={d.id} value={String(d.id)}>{d.team_name || d.name}</Option>
              ))}
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600 dark:text-gray-400">عنوان شغلی:</span>
            <Select value={jobTitle} onChange={(v) => onFiltersChange?.({ designation: v })} style={{ width: 180, fontFamily: 'CustomFont' }}>
              <Option value="all">همه</Option>
              {designations.map((d) => (
                <Option key={d.id} value={String(d.id)}>{d.name}</Option>
              ))}
            </Select>
          </div>
        </Space>
      </Card>

      <div className="mb-4 flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
        <span className="font-semibold text-gray-700 dark:text-gray-200">راهنما:</span>
        {legendItems.map((item) => {
          const palette = getAttendancePalette(item.key);
          return (
            <span key={item.key} className="inline-flex items-center gap-1.5">
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 22,
                  height: 22,
                  borderRadius: 5,
                  backgroundColor: palette?.bg,
                  color: palette?.color,
                  border: `1px solid ${palette?.border || 'transparent'}`,
                  fontSize: 12,
                }}
              >
                {statusIcons[item.key]}
              </span>
              <span style={{ fontSize: 12 }}>{item.label}</span>
            </span>
          );
        })}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
        <Table
          columns={columns}
          dataSource={attendanceData}
          rowKey="id"
          pagination={{
            pageSize: filters.pageSize || 30,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '50', '100'],
            showTotal: (total, range) => `نمایش ${range[0]} تا ${range[1]} از ${total} رکورد`,
            onShowSizeChange: (_, size) => onFiltersChange?.({ pageSize: size }),
          }}
          scroll={{ x: 'max-content' }}
          loading={loading}
          locale={{
            emptyText: (
              <Empty
                description={<span style={{ fontFamily: 'CustomFont' }}>هیچ رکوردی یافت نشد</span>}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          style={{ fontFamily: 'CustomFont' }}
          size="small"
        />
      </div>
    </div>
  );
};

export default AttendanceByHourView;
